import { useEffect, useRef, type ReactNode } from 'react';
import { CreditCard, Hourglass } from 'lucide-react';
import { PAYMENT_METHOD_LABELS } from '@shared/ledger';
import type { TraderTransaction } from '@/types/api';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { clock, maskedCard, relativeTime, rub, usdt } from '@/lib/format';
import { useCountdown, type Urgency } from '@/hooks/useCountdown';

/**
 * A pending payment: the densest, highest-stakes object in the product.
 *
 * The amount is the largest thing on the row and the countdown is the second.
 * Urgency is carried by a left edge and the countdown's own colour — never by
 * animation or a flashing background, because a trader watches this list for
 * hours and a blinking row would be unusable.
 */
const EDGE: Record<Urgency, string> = {
  calm: 'before:bg-line-strong',
  warning: 'before:bg-warn-500',
  critical: 'before:bg-danger-500',
  expired: 'before:bg-line',
};

const CLOCK_TONE: Record<Urgency, string> = {
  calm: 'text-fg',
  warning: 'text-warn-700',
  critical: 'text-danger-700',
  expired: 'text-fg-muted',
};

/** Wording differs per tier so urgency is never signalled by colour alone. */
const URGENCY_HINT: Record<Urgency, string> = {
  calm: 'Подтверждайте, только когда деньги уже на счёте.',
  warning: 'Меньше десяти минут — проверьте банк прямо сейчас.',
  critical: 'Меньше пяти минут. Подтвердите или сообщите о проблеме, пока срок не истёк.',
  expired: 'Этот платёж больше нельзя подтвердить.',
};

export interface PaymentCardProps {
  transaction: TraderTransaction;
  /** `performance.now()` reading from when this data landed. */
  syncedAt: number;
  onConfirm: (transaction: TraderTransaction) => void;
  onDispute: (transaction: TraderTransaction) => void;
  /**
   * Called when this payment crosses an urgency threshold. The parent owns a
   * single live region, so fifty rows don't mean fifty live regions.
   */
  onUrgencyChange?: (transaction: TraderTransaction, urgency: Urgency) => void;
  isBusy?: boolean;
}

export function PaymentCard({
  transaction,
  syncedAt,
  onConfirm,
  onDispute,
  onUrgencyChange,
  isBusy = false,
}: PaymentCardProps) {
  const { secondsLeft, urgency, isExpired } = useCountdown(transaction.secondsRemaining, syncedAt);

  // The digits tick once a second and are deliberately not a live region — that
  // would be unusable. Threshold crossings are the meaningful events, and they
  // are the only thing reported upward for announcement.
  const previousUrgency = useRef<Urgency>(urgency);
  const reportUrgency = useRef(onUrgencyChange);
  reportUrgency.current = onUrgencyChange;

  useEffect(() => {
    if (previousUrgency.current === urgency) return;
    previousUrgency.current = urgency;
    reportUrgency.current?.(transaction, urgency);
  }, [urgency, transaction]);

  return (
    <li
      className={cn(
        'relative bg-surface transition-colors duration-fast',
        // Left edge as a pseudo-element so it never affects row height.
        'before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-[""]',
        EDGE[urgency],
        isExpired && 'opacity-70',
      )}
    >
      {/*
        DOM order is the phone's reading order — amount, which card it landed
        on, how long is left, then the two actions — because that is the
        sequence a trader checks against their banking app. The merchant and the
        reference are evidence for a dispute, not part of the decision, so they
        come after the buttons. `col-start`/`row-start` put the same four blocks
        back into the dense two- and three-column desktop arrangement without
        reordering the markup.
      */}
      <div
        className={cn(
          'grid gap-x-8 gap-y-4 px-4 py-4 sm:px-5',
          'md:grid-cols-[minmax(0,1fr)_auto] md:items-start',
          // 16.5rem: "1 000 000,00 ₽" is fourteen glyphs of mono at --text-2xl.
          'lg:grid-cols-[minmax(0,16.5rem)_minmax(0,1fr)_auto]',
        )}
      >
        {/* 1 — the amount, the largest thing on the screen */}
        <div className="order-1 min-w-0 md:col-start-1 md:row-start-1 lg:row-span-2">
          <p className="eyebrow">Сумма</p>
          {/* Below 360px a seven-figure amount at --text-2xl is physically
              wider than the viewport, and mono digits have no break opportunity
              to fall back on. One step down on that one class of device is the
              only way the figure stays whole. */}
          <p className="num mt-1 whitespace-nowrap text-2xl font-medium leading-tight tracking-tight text-fg max-[359px]:text-xl">
            {rub(transaction.amountRub)}
          </p>
          <p className="num mt-1 text-xs text-fg-muted">
            заморожено {usdt(transaction.amountUsdt)}
          </p>
          {/* What this payment earns the trader. Working a list of payments
              without seeing your own cut is working blind, and this is the row
              where the decision actually gets made. Absent rather than zero
              when the server has not sent it: a confident 0,00 would be read
              as "you earned nothing". */}
          {transaction.commissionUsdt === undefined ? null : (
            <p className="mt-2 text-sm text-fg">
              Ваша комиссия{' '}
              <span className="num font-medium">{transaction.traderMarkupRub != null ? rub(transaction.traderMarkupRub) : usdt(transaction.commissionUsdt)}</span>
            </p>
          )}
        </div>

        {/* 2 — which card the money should be on */}
        <dl className="order-2 min-w-0 md:col-start-1 md:row-start-2 lg:col-start-2 lg:row-start-1">
          <dt className="eyebrow">На карту</dt>
          <dd className="mt-1 flex items-center gap-1.5 text-md font-medium text-fg">
            <CreditCard
              size={15}
              strokeWidth={1.5}
              aria-hidden="true"
              className="shrink-0 text-fg-faint"
            />
            <span className="num min-w-0 truncate">
              {transaction.cardBank ? `${transaction.cardBank} · ` : ''}
              {maskedCard(transaction.cardLast4)}
            </span>
          </dd>
          {/* Which rate this payment is priced at, named the way the
              commission block on Финансы names it. */}
          {transaction.method === undefined ? null : (
            <dd className="mt-1 text-xs text-fg-muted">
              Метод: {PAYMENT_METHOD_LABELS[transaction.method]}
            </dd>
          )}
        </dl>

        {/* 3 — the clock, then the two commitments */}
        <div
          className={cn(
            'order-3 flex min-w-0 flex-col border-t border-line pt-4',
            'md:col-start-2 md:row-start-1 md:row-span-3 md:min-w-[16.5rem]',
            'md:border-l md:border-t-0 md:pl-8 md:pt-0',
            'lg:col-start-3 lg:row-span-2',
          )}
        >
          <div>
            <p className="eyebrow flex items-center gap-1.5">
              <Hourglass size={11} strokeWidth={1.5} aria-hidden="true" />
              {isExpired ? 'Срок истёк' : 'Осталось'}
            </p>
            {/* One step below the amount. Both were --text-2xl, and because
                "29 мин 36 с" is the longer string it read as the louder one —
                the money has to win that contest.
                The block is full-width and left-aligned, so the string losing a
                glyph at 10:00 → 9:59 cannot shift anything around it. */}
            <p
              className={cn(
                'num mt-1 whitespace-nowrap text-xl font-medium leading-tight tracking-tight tabular-nums',
                CLOCK_TONE[urgency],
              )}
              // Announce only on a meaningful change, not every second.
              aria-live="off"
            >
              {isExpired ? clock(0) : clock(secondsLeft)}
            </p>
            {/* Every urgency tier gets distinct wording, so the state is never
                carried by colour alone. */}
            <p className="mt-1 text-xs text-fg-muted">{URGENCY_HINT[urgency]}</p>
          </div>

          {!isExpired ? (
            <>
              {/* Both buttons commit real money in opposite directions, so they
                  never sit side by side and never share an edge: full width,
                  a hairline between them, and fill versus outline so the thumb
                  is aiming at two visibly different objects. */}
              <div className="mt-4">
                <Button
                  variant="confirm"
                  size="lg"
                  fullWidth
                  disabled={isBusy}
                  onClick={() => onConfirm(transaction)}
                >
                  Подтвердить получение
                </Button>
              </div>
              <div className="mt-4 border-t border-line pt-4">
                <Button
                  variant="danger"
                  size="lg"
                  fullWidth
                  disabled={isBusy}
                  onClick={() => onDispute(transaction)}
                >
                  Деньги не пришли
                </Button>
              </div>
            </>
          ) : (
            <p className="mt-3 rounded-sm border border-line bg-sunken px-2.5 py-2 text-xs text-fg-secondary">
              Истёкшие платежи закрывает администратор. Если деньги всё же пришли, напишите в
              поддержку.
            </p>
          )}
        </div>

        {/* 4 — evidence for a dispute, not part of the decision */}
        <dl className="order-4 min-w-0 space-y-1.5 md:col-start-1 md:row-start-3 lg:col-start-2 lg:row-start-2 lg:mt-2">
          <Row label="Мерчант">
            <span className="block truncate">{transaction.merchantName}</span>
          </Row>
          <Row label="Номер">
            <span className="num break-all">{transaction.reference}</span>
          </Row>
          <Row label="Поступил">
            <time dateTime={transaction.createdAt} className="text-fg-secondary">
              {relativeTime(transaction.createdAt)}
            </time>
          </Row>
        </dl>
      </div>
    </li>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-20 shrink-0 text-fg-muted">{label}</dt>
      <dd className="min-w-0 flex-1 text-fg">{children}</dd>
    </div>
  );
}

